import axios from 'axios';
import { API } from '@/utils/api.utils';

export interface HighSchool {
  id: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  telephone?: string;
  enrollment?: number;
  st_grade?: string;
  end_grade?: string;
  ft_teachers?: number;
  ncesid?: string;
  latitude?: number;
  longitude?: number;
  website?: string;
}

export interface HighSchoolSuggestion {
  id: number;
  name: string;
  city?: string;
  state?: string;
  enrollment?: number;
}

export interface HighSchoolSearchRequest {
  query: string;
  state?: string;
  city?: string;
  limit?: number;
  offset?: number;
}

export interface HighSchoolSearchResponse {
  schools: HighSchool[];
  total_count: number;
  query: string;
}

class HighSchoolSearchService {
  private baseURL = `${API.BASE_URL}/api/v1/high-school-search`;

  /**
   * Search for high schools by name, city, or other criteria
   */
  async searchHighSchools(params: HighSchoolSearchRequest): Promise<HighSchoolSearchResponse> {
    try {
      const response = await axios.get(`${this.baseURL}/search`, {
        params: {
          q: params.query,
          state: params.state,
          city: params.city,
          limit: params.limit || 20,
          offset: params.offset || 0,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error searching high schools:', error);
      throw new Error('Failed to search high schools');
    }
  }

  /**
   * Search for high schools using similarity matching for better fuzzy search
   */
  async searchHighSchoolsWithSimilarity(
    query: string,
    state?: string,
    limit: number = 20
  ): Promise<HighSchool[]> {
    try {
      const response = await axios.get(`${this.baseURL}/search-similarity`, {
        params: {
          q: query,
          state,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error searching high schools with similarity:', error);
      throw new Error('Failed to search high schools');
    }
  }

  /**
   * Get high school suggestions for autocomplete
   */
  async suggestHighSchools(query: string, limit: number = 5): Promise<HighSchoolSuggestion[]> {
    try {
      if (!query || query.length < 2) {
        return [];
      }

      const response = await axios.get(`${this.baseURL}/suggest`, {
        params: {
          q: query,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error getting high school suggestions:', error);
      return [];
    }
  }

  /**
   * Get a specific high school by ID
   */
  async getHighSchoolById(schoolId: number): Promise<HighSchool> {
    try {
      const response = await axios.get(`${this.baseURL}/${schoolId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting high school by ID:', error);
      throw new Error('Failed to get high school details');
    }
  }

  /**
   * Get a specific high school by NCES ID
   */
  async getHighSchoolByNcesId(ncesId: string): Promise<HighSchool> {
    try {
      const response = await axios.get(`${this.baseURL}/nces/${ncesId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting high school by NCES ID:', error);
      throw new Error('Failed to get high school details');
    }
  }

  /**
   * Get list of states that have high schools in the database
   */
  async getAvailableStates(): Promise<string[]> {
    try {
      const response = await axios.get(`${this.baseURL}/states`);
      return response.data;
    } catch (error) {
      console.error('Error getting available states:', error);
      return [];
    }
  }

  /**
   * Get high schools by location (state and optionally city)
   */
  async getHighSchoolsByLocation(
    state: string,
    city?: string,
    limit: number = 20
  ): Promise<HighSchool[]> {
    try {
      const response = await axios.get(`${this.baseURL}/location/${state}`, {
        params: {
          city,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error getting high schools by location:', error);
      throw new Error('Failed to get high schools by location');
    }
  }

  /**
   * Get popular high schools (by enrollment)
   */
  async getPopularHighSchools(state?: string, limit: number = 10): Promise<HighSchool[]> {
    try {
      const response = await axios.get(`${this.baseURL}/popular`, {
        params: {
          state,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error getting popular high schools:', error);
      return [];
    }
  }

  /**
   * Format school display name with location
   */
  formatSchoolDisplayName(school: HighSchool): string {
    let displayName = school.name;
    
    if (school.city && school.state) {
      displayName += ` - ${school.city}, ${school.state}`;
    } else if (school.city) {
      displayName += ` - ${school.city}`;
    } else if (school.state) {
      displayName += ` - ${school.state}`;
    }
    
    return displayName;
  }

  /**
   * Format school details for display
   */
  formatSchoolDetails(school: HighSchool): string {
    const details = [];
    
    if (school.enrollment) {
      details.push(`${school.enrollment} students`);
    }
    
    if (school.st_grade && school.end_grade) {
      details.push(`Grades ${school.st_grade}-${school.end_grade}`);
    }
    
    if (school.ft_teachers) {
      details.push(`${school.ft_teachers} teachers`);
    }
    
    return details.join(' • ');
  }
}

export const highSchoolSearchService = new HighSchoolSearchService();
export default highSchoolSearchService;